import * as Sentry from "@sentry/react";
import { useContext, useEffect, useRef } from "react";
import { Subscription, PublicationContext } from "centrifuge";

import { CentrifugeContext } from "./centrifuge-context";

import { Message } from "@/components/Messaging/types";
import { useUser } from "@/context/user-context";

const sentrySubscribedHandlers = new WeakSet<Subscription>();

const useCentrifugeChannel = (
  topic: string,
  event: string,
  callback: (message: Message) => void,
  enabled = true,
) => {
  const { user } = useUser();
  const { client } = useContext(CentrifugeContext) ?? {};
  const callbackRef = useRef(callback);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const channelName = `users:user#${user.id}`;

  useEffect(() => {
    if (!enabled || !client) {
      return;
    }

    const subscription: Subscription =
      client.getSubscription(channelName) ||
      client.newSubscription(channelName, {
        delta: "fossil",
        recoverable: true,
        positioned: true,
      });

    if (!sentrySubscribedHandlers.has(subscription)) {
      subscription.on("subscribed", ctx => {
        if (ctx.recovered === false) {
          Sentry.captureMessage("centrifugo_recovery_failure", {
            level: "warning",
            tags: {
              event_type: "centrifugo_recovery_failure",
            },
          });
        }
      });
      sentrySubscribedHandlers.add(subscription);
    }

    // Increase max listeners since multiple components may subscribe to the same channel
    const sub = subscription as unknown as {
      getMaxListeners?: () => number;
      setMaxListeners?: (n: number) => void;
    };
    const currentMax = sub.getMaxListeners?.() ?? 10;
    if (currentMax < 100) {
      sub.setMaxListeners?.(100);
    }

    // Create a stable handler reference for cleanup
    const handler = (ctx: PublicationContext) => {
      if (ctx.data.message_name === event) {
        callbackRef.current({
          name: ctx.data.message_name,
          data: ctx.data.data,
        });
      }
    };

    // Add listener
    subscription.on("publication", handler);

    // Subscribe if not already subscribed and not subscribing
    if (subscription.state === "unsubscribed") {
      subscription.subscribe();
    }

    return () => {
      // Remove the specific listener
      subscription.removeListener("publication", handler);

      // Only unsubscribe if no other listeners remain
      if (subscription.listeners("publication").length === 0) {
        subscription.unsubscribe();
      }
    };
  }, [client, enabled, event, channelName]);
};

export { useCentrifugeChannel };
